import { createSlice, PayloadAction } from '@reduxjs/toolkit/react'
import { DEFAULT_CAPABILITIES } from '../constants'
import { remove as removeFromArray } from 'lodash'
import { api as userApi } from './userApiSlice'
import { api } from './apiSlice'
import { getCapabilities } from '../core/helpers'

const initialState: IMapState & IWalkState & IUserState & IMediaState & {
  lastFetchedAppConfig: string
  activateProfileModalOpen: boolean
  selectedProfile?: IProfileModel
  showNewsletterPrompt: boolean
  toasts: IEchoesToast[]
  userData?: IUserModel
  subscriptionData?: TSubscriptionProfileModel[]
  showRequestToSellModal: boolean
  showMarkdown: boolean
  walkSharingModalOpen: boolean
} = {
  lastFetchedAppConfig: new Date().toISOString(),
  activateProfileModalOpen: false,
  showNewsletterPrompt: false,
  viewport: {},
  currentUserGeolocation: undefined,
  focusLocation: undefined,
  mapVisible: true,
  mapNeedsRefresh: false,
  mapFullscreen: false,
  mapLayer: 'street',
  focusedEcho: -1,
  selectedEcho: -1,
  selectedTrajectory: -1,
  echoToDelete: -1,
  trajectoryToDelete: -1,
  hiddenEchoSlugs: [],
  hoveringEchoSlug: undefined,
  params: {
    limit: 100,
    skip: 0,
  },
  count: 0,
  createCollectionModalOpen: false,
  deleteCollectionModalOpen: false,
  selectedCollectionForDeletion: '',
  moveCollectionModalOpen: false,
  moveEchoesModalOpen: false,
  moveEchoModalOpen: false,
  echoIndexToMove: -1,
  quickProfileModalOpen: false,
  collectionEditTab: 'echoes',
  echoFilterText: '',
  importGpxKMLGeoJsonModalOpen: false,
  importedGpxKmlGeoJson: undefined,
  lang: 'en',
  capabilities: DEFAULT_CAPABILITIES,
  walkSubscriptionType: 'none',
  elementAdvancedOptionsOpen: '',
  toasts: [],
  showRequestToSellModal: false,
  showMarkdown: false,
  walkSharingModalOpen: false
}

const creatorSlice = createSlice({
  name: 'creator',
  initialState,
  reducers: {
    changeMapViewport(state, action) {
      state.viewport = action.payload
    },
    setUserGeolocation(state, action: PayloadAction<[number, number]>) {
      state.currentUserGeolocation = action.payload
    },
    setFocusLocation(state, action: PayloadAction<[number, number]>) {
      state.focusLocation = action.payload
    },
    toggleMapVisible(state) {
      state.mapNeedsRefresh = !state.mapVisible
      state.mapVisible = !state.mapVisible
      //state.mapFullscreen = state.mapVisible ? false : state.mapFullscreen
    },
    toggleMapFullscreen(state) {
      state.mapNeedsRefresh = true
      state.mapFullscreen = !state.mapFullscreen
      if (state.mapFullscreen) {
        // always set the map to visible if we want fullscreen
        state.mapVisible = true
      }
    },
    setMapLayer(state, action: PayloadAction<TEchoesMapLayer>) {
      state.mapNeedsRefresh = true
      state.mapLayer = action.payload
    },
    focusEcho(state, action: PayloadAction<number>) {
      state.focusedEcho = action.payload
    },
    selectEcho(state, action: PayloadAction<number>) {
      state.selectedEcho = action.payload
    },
    deselectEcho(state) {
      state.selectedEcho = -1
    },
    toggleHideEchoSlug(state, action: PayloadAction<string>) {
      if (action.payload) {
        const indexOfSlug = state.hiddenEchoSlugs.indexOf(action.payload)
        if (indexOfSlug > -1) {
          // remove
          state.hiddenEchoSlugs.splice(indexOfSlug, 1)
        } else {
          // add
          state.hiddenEchoSlugs.push(action.payload)
        }
      }
    },
    hoverEchoSlug(state, action: PayloadAction<string>) {
      state.hoveringEchoSlug = action.payload
    },
    selectLine(state, action: PayloadAction<number>) {
      state.selectedTrajectory = action.payload
    },
    deselectLine(state) {
      state.selectedTrajectory = -1
    },
    setDeleteCollectionModalOpen(state, action: PayloadAction<boolean>) {
      state.deleteCollectionModalOpen = action.payload
    },
    setCreateCollectionModalOpen(state, action: PayloadAction<boolean>) {
      state.createCollectionModalOpen = action.payload
    },
    resetCollectionModals(state) {
      state.deleteCollectionModalOpen = false
      state.echoToDelete = -1
      state.createCollectionModalOpen = false
      state.quickProfileModalOpen = false
    },
    setMoveCollectionModalOpen(state, action: PayloadAction<boolean>) {
      state.moveCollectionModalOpen = action.payload
    },
    setMoveEchoesModalOpen(state, action: PayloadAction<boolean>) {
      state.moveEchoesModalOpen = action.payload
    },
    setMoveEchoModalOpen(state, action: PayloadAction<{ open: boolean, echoIndex: number }>) {
      state.moveEchoModalOpen = action.payload.open
      state.echoIndexToMove = action.payload.echoIndex
    },
    setQuickProfileModal(state, action: PayloadAction<boolean>) {
      state.quickProfileModalOpen = action.payload
    },
    setActivateProfileModal(state, action) {
      state.activateProfileModalOpen = action.payload
    },
    setSelectedProfile(state, action: PayloadAction<IProfileModel>) {
      state.selectedProfile = action.payload
    },
    setEchoToDelete(state, action: PayloadAction<number>) {
      state.echoToDelete = action.payload
    },
    setTrajectoryToDelete(state, action: PayloadAction<number>) {
      state.trajectoryToDelete = action.payload
    },
    setImportGpxKmlGeoJsonModalOpen(state, action: PayloadAction<boolean>) {
      state.importGpxKMLGeoJsonModalOpen = action.payload
    },
    setImportedGpxKmlGeojson(state, action) {
      state.importedGpxKmlGeoJson = action.payload
    },
    setEchoFilterText(state, action: PayloadAction<string>) {
      state.echoFilterText = action.payload
    },
    setLang(state, action: PayloadAction<string>) {
      state.lang = action.payload
    },
    setUserCapabilities(state, action: PayloadAction<IUserCapabilities>) {
      state.capabilities = action.payload
    },
    setWalkSubscriptionType(state, action: PayloadAction<TSubscriptionType>) {
      state.walkSubscriptionType = action.payload
    },
    toggleElementAdvancedOptions(state) {
      state.elementAdvancedOptionsOpen = state.elementAdvancedOptionsOpen ? '' : 'elementAdvancedOptions'
    },
    addToast(state, action: PayloadAction<IEchoesToast>) {
      state.toasts.push(action.payload)
    },
    removeToast(state, action: PayloadAction<string>) {
      const i = state.toasts.findIndex((t) => t.id === action.payload)
      if (i > -1) {
        state.toasts = removeFromArray(state.toasts, i)
      }
    },
    setRequestToSellModal(state, action: PayloadAction<boolean>) {
      state.showRequestToSellModal = action.payload
    },
    toggleShowMarkdown(state) {
      state.showMarkdown = !state.showMarkdown
    },
    setWalkSharingModal(state, action: PayloadAction<boolean>) {
      state.walkSharingModalOpen = action.payload
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      userApi.endpoints.getMyUser.matchFulfilled,
      (state, action) => {
        // Store the user data and update capabilities if we have subscription data
        state.userData = action.payload

        if (state.subscriptionData) {
          try {
            const capabilities = getCapabilities(action.payload, state.subscriptionData)
            state.capabilities = capabilities
          } catch (e) {
            console.error('Error updating capabilities from user data:', e)
          }
        }
      }
    )

    builder.addMatcher(
      api.endpoints.getMySubscriptions.matchFulfilled,
      (state, action) => {
        // Store the subscription data and update capabilities if we have user data
        state.subscriptionData = action.payload

        if (state.userData) {
          try {
            const capabilities = getCapabilities(state.userData, action.payload)
            state.capabilities = capabilities
          } catch (e) {
            console.error('Error updating capabilities from subscription data:', e)
          }
        }
      }
    )
  }
})

export const {
  changeMapViewport,
  setUserGeolocation,
  setFocusLocation,
  toggleMapVisible,
  toggleMapFullscreen,
  setMapLayer,
  focusEcho,
  selectEcho,
  deselectEcho,
  toggleHideEchoSlug,
  hoverEchoSlug,
  selectLine,
  deselectLine,
  setDeleteCollectionModalOpen,
  setCreateCollectionModalOpen,
  resetCollectionModals,
  setMoveEchoesModalOpen,
  setMoveEchoModalOpen,
  setQuickProfileModal,
  setActivateProfileModal,
  setSelectedProfile,
  setEchoToDelete,
  setTrajectoryToDelete,
  setImportGpxKmlGeoJsonModalOpen,
  setImportedGpxKmlGeojson,
  setEchoFilterText,
  setLang,
  setUserCapabilities,
  setWalkSubscriptionType,
  toggleElementAdvancedOptions,
  addToast,
  removeToast,
  setRequestToSellModal,
  toggleShowMarkdown,
  setWalkSharingModal
} = creatorSlice.actions

export default creatorSlice.reducer
